import { useMemo } from 'react';
import { TableColumn } from './types';
import { getStickyOffsets } from './utils';
import {
  HeaderScrollWrapper,
  HeaderContainer,
  ParentCell,
  StickyParentCell,
  ChildCell,
  ExpandCollapseButton,
  CollapseIcon,
  ExpandIcon,
} from './HeaderStyles';
import useCellWidthCalculation from './useCellWidthCalculation';

interface Props {
  expandedColumns: Set<string>;
  columns: TableColumn[];
  onExpand?: (columnKey: string) => void;
  onCollapse?: (columnKey: string) => void;
  scrollRef: (el: HTMLDivElement | null) => void;
}

const Header = ({ scrollRef, columns, expandedColumns, onExpand, onCollapse }: Props) => {
  const { gridTemplateColumns, minWidth } = useCellWidthCalculation(columns, expandedColumns);
  const stickyOffsets = useMemo(() => getStickyOffsets(columns), [columns]);

  const hasSubColumns = columns.some((col) => col.children?.length);

  return (
    <HeaderScrollWrapper ref={scrollRef}>
      <HeaderContainer
        sx={{
          gridTemplateColumns,
          gridTemplateRows: hasSubColumns ? 'auto auto' : 'auto',
          minWidth: `${minWidth}px`,
        }}
      >
        {columns.map((column) => {
          const hasChildren = !!column.children?.length;
          const isExpanded = hasChildren && expandedColumns.has(column.key);
          const stickyLeft = stickyOffsets.get(column.key);
          const CellComponent = stickyLeft !== undefined ? StickyParentCell : ParentCell;

          const isCollapsed = hasChildren && !isExpanded;

          return (
            <CellComponent
              key={column.key}
              sx={{
                gridColumn: isExpanded ? `span ${column.children!.length}` : undefined,
                gridRow: !hasChildren && hasSubColumns ? 'span 2' : undefined,
                ...(stickyLeft !== undefined && { left: stickyLeft }),
                ...(isCollapsed && { backgroundColor: '#265C58' }),
              }}
            >
              {column.renderHeader ? column.renderHeader(column) : column.label}
              {hasChildren && onExpand && onCollapse && (
                <ExpandCollapseButton
                  onClick={() => (isExpanded ? onCollapse(column.key) : onExpand(column.key))}
                >
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </ExpandCollapseButton>
              )}
            </CellComponent>
          );
        })}

        {hasSubColumns &&
          columns.flatMap((column) => {
            if (!column.children?.length) return [];
            if (expandedColumns.has(column.key)) {
              return column.children.map((child) => (
                <ChildCell key={child.key}>{child.label}</ChildCell>
              ));
            }
            const lastChild = column.children[column.children.length - 1];
            return [
              <ChildCell key={lastChild.key} sx={{ backgroundColor: '#265C58' }}>
                {lastChild.label}
              </ChildCell>,
            ];
          })}
      </HeaderContainer>
    </HeaderScrollWrapper>
  );
};

export default Header;
